import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ChevronLeft, Camera, X, CheckCircle2, Loader2,
  AlertTriangle, ReceiptText, RefreshCw,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { STORAGE_BUCKETS } from '@/config/constants'
import { ROUTES } from '@/config/routes'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'

const MAX_MB = 10

function fmt(n) {
  if (n == null || isNaN(n)) return null
  return '₱' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })
}

// ── Receipt type label ────────────────────────────────────────────────────────
function receiptLabel(type) {
  if (type === 'downpayment') return 'Downpayment'
  if (type === 'balance')     return 'Balance Payment'
  return 'Full Payment'
}

export default function UploadPayment() {
  const { proposalId } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)
  const fileInputRef = useRef(null)

  // Page state
  const [loading,     setLoading]    = useState(true)
  const [proposal,    setProposal]   = useState(null)   // final_proposals row
  const [receiptType, setReceiptType] = useState(null)  // 'downpayment' | 'balance' | 'full'
  const [pageError,   setPageError]  = useState(null)

  // Form state
  const [photo,       setPhoto]      = useState(null)   // { file, previewUrl }
  const [reference,   setReference]  = useState('')
  const [amountPaid,  setAmountPaid] = useState('')
  const [errors,      setErrors]     = useState({})
  const [submitting,  setSubmitting] = useState(false)
  const [done,        setDone]       = useState(false)

  // ── Load proposal ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!proposalId || !profile?.id) return

    async function load() {
      setLoading(true)

      const { data: fp, error: fpErr } = await supabase
        .from('final_proposals')
        .select('id, booking_id, status, payment_type, total_amount, downpayment_pct')
        .eq('id', proposalId)
        .single()

      if (fpErr || !fp) {
        setPageError('Proposal not found. Please check your projects page.')
        setLoading(false)
        return
      }

      setProposal(fp)

      // Determine which receipt type is still pending for this proposal
      const { data: existing } = await supabase
        .from('payment_receipts')
        .select('receipt_type, status')
        .eq('proposal_id', proposalId)
        .eq('customer_id', profile.id)

      const existingTypes = (existing ?? []).map((r) => r.receipt_type)

      if (fp.payment_type === 'full') {
        setReceiptType('full')
      } else if (fp.payment_type === 'downpayment') {
        const dpDone = existingTypes.includes('downpayment')
        setReceiptType(dpDone ? 'balance' : 'downpayment')
      } else {
        setReceiptType('downpayment')
      }

      setLoading(false)
    }

    load()
  }, [proposalId, profile?.id]) // eslint-disable-line

  // ── Photo capture ──────────────────────────────────────────────────────────
  function handlePhotoSelect(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, photo: `Photo must be under ${MAX_MB}MB` }))
      return
    }
    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl)
    setPhoto({ file, previewUrl: URL.createObjectURL(file) })
    setErrors((p) => ({ ...p, photo: null }))
    e.target.value = ''
  }

  function clearPhoto() {
    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl)
    setPhoto(null)
  }

  // ── Amount field — numbers only ────────────────────────────────────────────
  function handleAmountChange(e) {
    const raw = e.target.value.replace(/[^0-9.]/g, '')
    // Allow only one decimal point
    const parts = raw.split('.')
    const clean = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : raw
    setAmountPaid(clean)
    setErrors((p) => ({ ...p, amount: null }))
  }

  // ── Validate ───────────────────────────────────────────────────────────────
  function validate() {
    const errs = {}
    if (!photo)              errs.photo     = 'Please capture or upload your receipt photo'
    if (!reference.trim())   errs.reference = 'Reference number is required'
    if (!amountPaid.trim() || isNaN(Number(amountPaid)) || Number(amountPaid) <= 0)
                             errs.amount    = 'Enter a valid amount'
    return errs
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    setSubmitting(true)
    try {
      // 1. Upload receipt image to storage
      const ext  = photo.file.name.split('.').pop() || 'jpg'
      const path = `${profile.id}/${proposalId}/${receiptType}_${Date.now()}.${ext}`

      const { error: uploadErr } = await supabase.storage
        .from(STORAGE_BUCKETS.RECEIPTS)
        .upload(path, photo.file, { upsert: false })

      if (uploadErr) throw uploadErr

      // 2. Insert payment_receipts row
      const { error: insertErr } = await supabase
        .from('payment_receipts')
        .insert({
          proposal_id:        proposalId,
          customer_id:        profile.id,
          receipt_type:       receiptType,
          receipt_image_path: path,
          payment_reference:  reference.trim(),
          amount_stated:      Number(amountPaid),
          status:             'pending',
        })

      if (insertErr) throw insertErr
      supabase.functions.invoke('send-notification', { body: {
        type: 'admin_payment_uploaded', customer_id: profile.id,
        data: { ref_number: proposalId.slice(0, 8).toUpperCase(), amount: amountPaid ? `PHP ${Number(amountPaid).toLocaleString()}` : null },
      }}).catch(console.error)
      setDone(true)
    } catch (err) {
      console.error('Payment upload error:', err)
      setErrors({ submit: `Upload failed: ${err?.message ?? 'Please try again.'}` })
    } finally {
      setSubmitting(false)
    }
  }

  // ── Amount owed display ────────────────────────────────────────────────────
  function amountOwed() {
    if (!proposal) return null
    const total = proposal.total_amount
    const pct   = proposal.downpayment_pct ?? 50
    if (receiptType === 'full')        return total
    if (receiptType === 'downpayment') return Math.round(total * pct / 100)
    if (receiptType === 'balance')     return Math.round(total * (100 - pct) / 100)
    return null
  }

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  if (pageError) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-4 px-6 text-center">
        <AlertTriangle className="w-8 h-8 text-siglat-red" />
        <p className="text-ink-primary font-semibold">{pageError}</p>
        <button
          onClick={() => navigate(ROUTES.BOOK_HISTORY)}
          className="text-sm text-siglat-teal hover:underline"
        >
          Back to My Projects
        </button>
      </div>
    )
  }

  // ── Success screen ─────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-dvh flex flex-col bg-surface-base">
        <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top">
          <div className="page-container">
            <p className="text-sm text-ink-muted">Payment Receipt</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
          <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/25 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-ink-primary mb-2">
              Receipt Submitted
            </p>
            <p className="text-sm text-ink-muted leading-relaxed">
              Siglat Konstract will verify your payment within 1 business day.
              You will be notified once confirmed.
            </p>
          </div>
          <div className="bg-surface-raised border border-surface-border rounded-xl px-4 py-3 w-full max-w-xs text-sm text-ink-muted leading-relaxed">
            <p>Reference: <span className="text-ink-primary font-medium">{reference}</span></p>
            <p className="mt-0.5">Amount: <span className="text-ink-primary font-medium">{fmt(Number(amountPaid))}</span></p>
            <p className="mt-0.5">Type: <span className="text-ink-primary font-medium">{receiptLabel(receiptType)}</span></p>
          </div>
          <Button
            fullWidth
            size="lg"
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
          >
            Back to My Projects
          </Button>
        </div>
      </div>
    )
  }

  const owed = amountOwed()

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container">
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-3 -ml-1"
          >
            <ChevronLeft className="w-4 h-4" />
            My Projects
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-siglat-teal/10 border border-siglat-teal/25 flex items-center justify-center shrink-0">
              <ReceiptText className="w-5 h-5 text-siglat-teal" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-ink-primary leading-tight">
                Upload Payment Receipt
              </h1>
              <p className="text-xs text-ink-muted">{receiptLabel(receiptType)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-5">

          {/* Amount card */}
          {owed != null && (
            <div className="bg-surface-raised border border-siglat-teal/30 rounded-xl px-4 py-4">
              <p className="text-xs text-ink-muted uppercase tracking-widest mb-1">
                {receiptLabel(receiptType)} Required
              </p>
              <p className="font-display text-3xl font-bold text-siglat-teal">
                {fmt(owed)}
              </p>
              <p className="text-xs text-ink-muted mt-1">
                Send via GCash or bank deposit — then photograph your receipt below.
              </p>
            </div>
          )}

          {/* GCash account reminder */}
          <div className="bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 flex flex-col gap-0.5">
            <p className="text-xs text-ink-muted uppercase tracking-widest mb-1">Send Payment To</p>
            <p className="text-sm text-ink-primary font-semibold">GCash / Maya</p>
            <p className="text-sm text-ink-primary">Siglat Konstract OPC</p>
            <p className="text-xs text-ink-muted mt-1">
              Account number will be provided by Siglat Konstract via your project chat.
            </p>
          </div>

          {/* Photo capture */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-ink-secondary">
              Receipt Photo <span className="text-siglat-red">*</span>
            </label>

            {!photo ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-3 border-2 border-dashed border-surface-border rounded-2xl py-10 px-6 active:scale-[0.98] transition-all hover:border-siglat-teal/50 hover:bg-surface-overlay"
              >
                <div className="w-14 h-14 rounded-full bg-siglat-teal/10 border border-siglat-teal/25 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-siglat-teal" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-ink-primary">Take Photo or Upload</p>
                  <p className="text-xs text-ink-muted mt-0.5">
                    Opens camera on mobile — or choose from gallery
                  </p>
                </div>
              </button>
            ) : (
              <div className="relative rounded-2xl overflow-hidden border border-surface-border">
                <img
                  src={photo.previewUrl}
                  alt="Receipt preview"
                  className="w-full object-contain max-h-72 bg-surface-overlay"
                />
                {/* Retake button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/70 text-white text-xs font-medium px-3 py-2 rounded-full backdrop-blur-sm"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retake
                </button>
                {/* Remove button */}
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="absolute top-3 right-3 bg-black/60 rounded-full p-1.5"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            )}

            {errors.photo && (
              <p className="text-xs text-siglat-red">⚠ {errors.photo}</p>
            )}

            {/* Hidden file input — camera capture on mobile */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoSelect}
            />
          </div>

          {/* Reference number */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-secondary">
              GCash / Bank Reference No. <span className="text-siglat-red">*</span>
            </label>
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              value={reference}
              onChange={(e) => {
                setReference(e.target.value)
                setErrors((p) => ({ ...p, reference: null }))
              }}
              placeholder="e.g. 123456789012"
              className={`w-full bg-surface-overlay border rounded-xl px-4 py-3.5 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal transition-colors
                ${errors.reference ? 'border-siglat-red' : 'border-surface-border'}`}
            />
            {errors.reference && (
              <p className="text-xs text-siglat-red">⚠ {errors.reference}</p>
            )}
          </div>

          {/* Amount paid */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-secondary">
              Amount Paid (₱) <span className="text-siglat-red">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted text-base select-none">₱</span>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={amountPaid}
                onChange={handleAmountChange}
                placeholder={owed ? String(owed) : '0'}
                className={`w-full bg-surface-overlay border rounded-xl pl-8 pr-4 py-3.5 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal transition-colors
                  ${errors.amount ? 'border-siglat-red' : 'border-surface-border'}`}
              />
            </div>
            {errors.amount && (
              <p className="text-xs text-siglat-red">⚠ {errors.amount}</p>
            )}
          </div>

          {/* Submit error */}
          {errors.submit && (
            <div className="bg-siglat-red/10 border border-siglat-red/30 rounded-xl px-4 py-3">
              <p className="text-sm text-siglat-red">{errors.submit}</p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-surface-raised border border-surface-border rounded-xl px-4 py-3">
            <p className="text-xs text-ink-muted leading-relaxed">
              Siglat Konstract will verify your receipt manually. Worker dispatch begins only after payment is confirmed. Do not submit a receipt for a different transaction.
            </p>
          </div>

        </div>
      </div>

      {/* Bottom CTA */}
      <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
        <div className="page-container">
          <Button
            fullWidth
            size="lg"
            loading={submitting}
            onClick={handleSubmit}
          >
            Submit Payment Receipt
          </Button>
        </div>
      </div>

    </div>
  )
}
